import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Modal } from '@cognitiv/cyprus-ui';
import classNames from 'classnames';
import { BottomNavigation } from 'components/bottom-navigation/BottomNavigation';
import { ModalHeader } from 'components/modal-header/ModalHeader';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectManageScheduleBatch } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { selectProjectId } from 'ducks/project/selectors';
import { selectScheduledBatch } from 'ducks/scheduled-batch/selectors';
import { clearScheduledBatch } from 'ducks/scheduled-batch/slices';
import { selectScheduledBatchParameters } from 'ducks/scheduled-batch-parameters/selectors';
import { updateSettings, updateSuccess } from 'ducks/settings/slices';
import { ProjectParameters } from 'ducks/types/store-types';
import { UnionFromArray } from 'ducks/types/type-helpers';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { ScheduledBatchFirstStep } from 'modals/batches/components/ScheduledBatchFirstStep';
import { useManageScheduleBatchContext } from 'modals/batches/context/ManageScheduleBatchContext';
import { useScheduleBatchSubmit } from 'modals/batches/hooks/useScheduleBatchSubmit';
import useForceUpdate from 'modals/parameters/components/hooks/useForceUpdate';
import { Parameters } from 'modals/parameters/components/Parameters';
import { shallowEqual } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleError } from 'utils/errors';
import { getFormWarnings } from 'utils/helpers';
import { mergeSearchQuery } from 'utils/queries';
import { v4 as uuidv4 } from 'uuid';

import { useStepper } from './hooks/usestepper';

import cn from 'modals/batches/components/Components.module.scss';

export const ManageScheduleBatchModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { theme } = useSwitchTheme();

  const manage_schedule_batch = useAppSelector(selectManageScheduleBatch, shallowEqual);
  const project_id = useAppSelector(selectProjectId, shallowEqual);
  const { scheduled_batch_id, is_active } = useAppSelector(selectScheduledBatch);
  const parameters = useAppSelector(selectScheduledBatchParameters);

  const [batch_parameters, setBatchParameters] = useState<ProjectParameters>([]);
  const { force_update, handleUpdateDataFromModal } = useForceUpdate(setBatchParameters);
  const { page, wizard_width, reset, onNextStep, onPrevStep } = useStepper(0);

  const { form, validate, errors } = useManageScheduleBatchContext();

  const onNext = useCallback(() => {
    const is_valid = validate();

    if (!is_valid && !!errors.length) {
      dispatch(setModal({ warning: true }));
      dispatch(updateSettings({ warning: getFormWarnings(errors) }));
    }

    if (is_valid) {
      onNextStep();
    }
  }, [dispatch, errors, onNextStep, validate]);

  const { submitScheduleBatch } = useScheduleBatchSubmit(project_id, scheduled_batch_id, is_active, batch_parameters);

  useEffect(() => {
    setBatchParameters(parameters);
  }, [parameters]);

  const closeModal = useCallback(() => {
    reset();
    dispatch(setModal({ manage_schedule_batch: false }));
    dispatch(clearScheduledBatch());
  }, [dispatch, reset]);

  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const is_valid = validate();

      if (!is_valid) return;

      try {
        const queries = mergeSearchQuery(search, { update: uuidv4() });

        const res: any = await submitScheduleBatch(form);
        if (res?.error) return;

        closeModal();
        navigate(`${pathname}${queries}`);
        dispatch(updateSuccess(`Scheduled Batch has been updated successfully`));
      } catch (err) {
        handleError(err);
      }
    },
    [closeModal, dispatch, form, navigate, pathname, search, submitScheduleBatch, validate],
  );

  const setParameters = useCallback(
    (row: UnionFromArray<ProjectParameters>, force_update?: boolean) => {
      handleUpdateDataFromModal(row, force_update);
    },
    [handleUpdateDataFromModal],
  );

  return (
    <Modal
      name="manage_schedule_batch"
      show={manage_schedule_batch}
      width={wizard_width}
      padding={0}
      close_box={50}
      onClose={closeModal}
      standard={false}
    >
      <ModalHeader onReturn={onPrevStep} page={page}>
        {scheduled_batch_id ? 'Update ' : 'Create '}
        Scheduled Batch
      </ModalHeader>
      <form
        className={classNames(cn.overrideWizard, cn.manageForm)}
        onSubmit={onSubmit}
        style={{ backgroundColor: theme.modal.modal_background }}
      >
        {page === 0 && <ScheduledBatchFirstStep />}
        {page === 1 && (
          <Parameters force_update={force_update} setParameters={setParameters} parameters={batch_parameters} />
        )}
        <BottomNavigation current={page} end={1} onBack={onPrevStep} onNext={onNext} save onClose={closeModal} />
      </form>
    </Modal>
  );
};
